import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, TrendingDown, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Badge } from "@/components/ui/badge";
import { fmtNum } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const TILES = [
  { key: "certified_revenue", label: "Certified Revenue" },
  { key: "actual_cost", label: "Actual Cost" },
  { key: "margin", label: "Margin" },
  { key: "cash_position", label: "Cash Position" },
  { key: "payables", label: "Payables" },
  { key: "receivables", label: "Receivables" },
];

export default function ExecutiveDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/reports/executive-dashboard").then(({ data }) => setData(data)).catch((err) => toast.error(getErrorMessage(err))).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6" data-testid="executive-dashboard-page">
      <PageHeader title="Executive Dashboard" description="Portfolio-wide revenue, cost, cash and risk in one view." />

      {loading || !data ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="executive-tiles">
            {TILES.map((tile) => (
              <div key={tile.key} className="rounded-xl border border-border bg-card p-4" data-testid={`tile-${tile.key}`}>
                <p className="text-xs text-muted-foreground">{tile.label}</p>
                <p className="mt-1 text-lg font-semibold text-foreground">₹{fmtNum(data.tiles[tile.key], 0)}</p>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-border bg-card">
            <div className="border-b border-border px-4 py-3"><h3 className="text-sm font-semibold text-foreground">Progress vs Cost</h3></div>
            {data.progress_vs_cost.length === 0 ? (
              <EmptyState title="No active projects" testId="progress-vs-cost-empty" />
            ) : (
              <div className="divide-y divide-border">
                {data.progress_vs_cost.map((row) => {
                  const aheadOfProgress = Number(row.cost_pct) > Number(row.progress_pct);
                  return (
                    <div key={row.project_id} className="flex items-center justify-between px-4 py-3" data-testid={`progress-vs-cost-row-${row.project_code}`}>
                      <div>
                        <p className="text-sm font-medium text-foreground">{row.project_code} — {row.project_name}</p>
                        <p className="text-xs text-muted-foreground">Progress {fmtNum(row.progress_pct, 1)}% · Cost {fmtNum(row.cost_pct, 1)}%</p>
                      </div>
                      {aheadOfProgress ? (
                        <Badge variant="destructive" className="gap-1"><TrendingUp className="h-3 w-3" /> Cost ahead of progress</Badge>
                      ) : (
                        <Badge variant="secondary" className="gap-1"><TrendingDown className="h-3 w-3" /> On track</Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-border bg-card">
              <div className="border-b border-border px-4 py-3"><h3 className="text-sm font-semibold text-foreground">Top Overdue Items</h3></div>
              {data.top_overdue.length === 0 ? (
                <EmptyState title="Nothing overdue" testId="top-overdue-empty" />
              ) : (
                <div className="divide-y divide-border">
                  {data.top_overdue.map((item) => (
                    <div key={item.id} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`overdue-item-${item.id}`}>
                      <span className="flex items-center gap-2 text-foreground"><AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> {item.title}</span>
                      <span className="font-mono text-xs text-muted-foreground">{item.days_overdue}d</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="rounded-xl border border-border bg-card">
              <div className="border-b border-border px-4 py-3"><h3 className="text-sm font-semibold text-foreground">Open Issues by Project</h3></div>
              {data.open_issues_by_project.length === 0 ? (
                <EmptyState title="No open issues" testId="open-issues-empty" />
              ) : (
                <div className="divide-y divide-border">
                  {data.open_issues_by_project.map((row) => (
                    <div key={row.project_code} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`open-issues-row-${row.project_code}`}>
                      <span className="text-foreground">{row.project_code}</span>
                      <Badge variant="secondary">{row.count}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
